// All sound is synthesised with the Web Audio API, so there are no audio files to fail to load.
// The AudioContext is created only after a user gesture (browser autoplay rules).

export class GameAudio {
  constructor() {
    this.ctx = null;
    this.muted = false;
    this.volume = 0.8;
    this.crowdOn = true;
    this.failed = false;
  }

  /** Call from a user gesture (click / tap / key). Safe to call many times. */
  unlock() {
    if (this.failed) return;
    try {
      if (!this.ctx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) { this.failed = true; return; }
        this.ctx = new AC();
        this.build();
      }
      if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    } catch {
      this.failed = true;
    }
  }

  build() {
    const c = this.ctx;
    this.master = c.createGain();
    this.master.connect(c.destination);
    this.sfx = c.createGain();
    this.sfx.connect(this.master);
    this.crowdBus = c.createGain();
    this.crowdBus.connect(this.master);
    this.applyVolume();

    // 2 s of white noise reused by every noisy sound
    const len = c.sampleRate * 2;
    this.noise = c.createBuffer(1, len, c.sampleRate);
    const d = this.noise.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;

    // crowd ambience: two filtered noise beds with a slow swell
    const bed = (freq, q, gain) => {
      const src = c.createBufferSource();
      src.buffer = this.noise;
      src.loop = true;
      src.playbackRate.value = 0.7 + Math.random() * 0.2;
      const f = c.createBiquadFilter();
      f.type = 'bandpass'; f.frequency.value = freq; f.Q.value = q;
      const g = c.createGain();
      g.gain.value = gain;
      src.connect(f).connect(g).connect(this.crowdBus);
      src.start();
      return g;
    };
    this.ambA = bed(520, 0.6, 0.16);
    this.ambB = bed(1400, 0.9, 0.05);
    const lfo = c.createOscillator();
    lfo.frequency.value = 0.13;
    const lfoGain = c.createGain();
    lfoGain.gain.value = 0.05;
    lfo.connect(lfoGain).connect(this.ambA.gain);
    lfo.start();
  }

  applyVolume() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.master.gain.setTargetAtTime(this.muted ? 0 : this.volume, t, 0.05);
    this.crowdBus.gain.setTargetAtTime(this.crowdOn ? 1 : 0, t, 0.1);
  }

  setMuted(m) { this.muted = m; this.applyVolume(); }
  setVolume(v) { this.volume = v; this.applyVolume(); }
  setCrowd(on) { this.crowdOn = on; this.applyVolume(); }

  /** Suspend when the tab is hidden; resume when visible. */
  setHidden(hidden) {
    if (!this.ctx) return;
    if (hidden) this.ctx.suspend().catch(() => {});
    else this.ctx.resume().catch(() => {});
  }

  get ready() { return !!this.ctx && !this.failed && this.ctx.state === 'running'; }

  env(gainNode, t, attack, peak, decay) {
    const g = gainNode.gain;
    g.setValueAtTime(0.0001, t);
    g.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
    g.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  }

  noiseHit({ type = 'bandpass', freq = 1000, q = 1, peak = 0.5, attack = 0.003, decay = 0.2, rate = 1, bus } = {}) {
    if (!this.ready) return;
    const c = this.ctx;
    const t = c.currentTime;
    const src = c.createBufferSource();
    src.buffer = this.noise;
    src.playbackRate.value = rate;
    const f = c.createBiquadFilter();
    f.type = type; f.frequency.value = freq; f.Q.value = q;
    const g = c.createGain();
    this.env(g, t, attack, peak, decay);
    src.connect(f).connect(g).connect(bus || this.sfx);
    src.start(t, Math.random() * 1.5);
    src.stop(t + attack + decay + 0.05);
    return { f, t };
  }

  tone({ freq = 440, to = null, type = 'sine', peak = 0.4, attack = 0.002, decay = 0.2, delay = 0 } = {}) {
    if (!this.ready) return;
    const c = this.ctx;
    const t = c.currentTime + delay;
    const o = c.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (to) o.frequency.exponentialRampToValueAtTime(to, t + attack + decay);
    const g = c.createGain();
    this.env(g, t, attack, peak, decay);
    o.connect(g).connect(this.sfx);
    o.start(t);
    o.stop(t + attack + decay + 0.05);
  }

  kick(power = 0.7) {
    this.tone({ freq: 150, to: 45, peak: 0.6 + power * 0.4, decay: 0.16 });
    this.noiseHit({ type: 'highpass', freq: 1800, peak: 0.35 + power * 0.3, decay: 0.05 });
    this.noiseHit({ type: 'lowpass', freq: 600, peak: 0.4, decay: 0.08 });
  }

  bounce(speed) {
    const p = Math.min(1, speed / 12);
    this.tone({ freq: 110, to: 60, peak: 0.25 * p + 0.03, decay: 0.09 });
  }

  post(speed) {
    const p = Math.min(1, speed / 25) * 0.4 + 0.1;
    for (const [f, d] of [[523, 0.9], [1247, 0.7], [2093, 0.45], [3160, 0.3]]) this.tone({ freq: f, peak: p * (f < 1000 ? 1 : 0.5), decay: d });
    this.noiseHit({ type: 'bandpass', freq: 2500, q: 2, peak: p, decay: 0.05 });
  }

  net(speed) {
    const p = Math.min(1, speed / 20);
    this.noiseHit({ type: 'bandpass', freq: 3200, q: 0.7, peak: 0.25 + p * 0.35, attack: 0.01, decay: 0.35 });
    this.noiseHit({ type: 'lowpass', freq: 400, peak: 0.25 * p, attack: 0.005, decay: 0.2 });
  }

  glove(speed) {
    const p = Math.min(1, speed / 25);
    this.noiseHit({ type: 'lowpass', freq: 900, peak: 0.5 + p * 0.4, decay: 0.12 });
    this.tone({ freq: 200, to: 90, peak: 0.35, decay: 0.1 });
  }

  boards(speed) {
    this.noiseHit({ type: 'bandpass', freq: 300, q: 1.5, peak: Math.min(0.8, speed / 15), decay: 0.25 });
  }

  whistle() {
    if (!this.ready) return;
    const c = this.ctx;
    const t = c.currentTime;
    const o = c.createOscillator();
    o.frequency.value = 2900;
    const lfo = c.createOscillator();
    lfo.frequency.value = 28;
    const lg = c.createGain();
    lg.gain.value = 120;
    lfo.connect(lg).connect(o.frequency);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
    g.gain.setValueAtTime(0.18, t + 0.32);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.42);
    o.connect(g).connect(this.sfx);
    o.start(t); lfo.start(t); o.stop(t + 0.45); lfo.stop(t + 0.45);
  }

  /** Crowd reactions: 'goal' roar, 'ooh' near miss / save, 'groan'. */
  crowd(kind, intensity = 1) {
    if (!this.ready || !this.crowdOn) return;
    const bus = this.crowdBus;
    if (kind === 'goal') {
      this.noiseHit({ type: 'bandpass', freq: 900, q: 0.5, peak: 0.55 * intensity, attack: 0.18, decay: 3.2, rate: 0.8, bus });
      this.noiseHit({ type: 'bandpass', freq: 2200, q: 0.8, peak: 0.22 * intensity, attack: 0.25, decay: 2.6, rate: 0.9, bus });
      this.noiseHit({ type: 'lowpass', freq: 350, peak: 0.35 * intensity, attack: 0.1, decay: 2.5, bus });
    } else if (kind === 'ooh') {
      const h = this.noiseHit({ type: 'bandpass', freq: 650, q: 3, peak: 0.45 * intensity, attack: 0.15, decay: 1.3, rate: 0.7, bus });
      if (h) h.f.frequency.exponentialRampToValueAtTime(380, h.t + 1.3);
    } else if (kind === 'groan') {
      const h = this.noiseHit({ type: 'bandpass', freq: 420, q: 2.5, peak: 0.35 * intensity, attack: 0.2, decay: 1.4, rate: 0.6, bus });
      if (h) h.f.frequency.exponentialRampToValueAtTime(220, h.t + 1.4);
    }
  }

  click() { this.tone({ freq: 880, to: 660, type: 'triangle', peak: 0.08, decay: 0.05 }); }
  tick() { this.tone({ freq: 1320, type: 'square', peak: 0.03, decay: 0.03 }); }
}
